import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Button, Form, TextArea, Message, Segment,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';

// Confirmation with a mandatory justification before the force call. The parent
// mounts this component only while the modal is open, so the justification
// below starts empty on every force.
function ForceConfirmationModal({
  open, loading, errorMessage, onClose, onConfirm, orderId, vin, productName,
  failedRulesCount, totalRulesCount,
}) {
  const [justification, setJustification] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleConfirm = () => {
    if (isEmpty(justification.trim())) {
      setValidationError('Justification is required to create a guarantee.');
      return;
    }
    setValidationError('');
    onConfirm(justification.trim());
  };

  const confirmDisabled = loading || isEmpty(justification.trim());

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeOnEscape={!loading}
      closeOnDimmerClick={false}
      size='small'
    >
      <Modal.Header>Force Guarantee Override</Modal.Header>
      <Modal.Content>
        <p>
          You are about to force a guarantee for a vehicle that failed
          eligibility checks. This action will be logged and attributed to your
          account
        </p>
        <Segment data-testid='force-guarantee-summary'>
          <div>
            <b>Order ID:</b>
            {' '}
            {orderId || '—'}
          </div>
          <div>
            <b>VIN:</b>
            {' '}
            {vin || '—'}
          </div>
          <div>
            <b>Product:</b>
            {' '}
            {productName || '—'}
          </div>
          <div>
            <b>Failed Rules:</b>
            {' '}
            {failedRulesCount}
            {' of '}
            {totalRulesCount}
          </div>
        </Segment>
        <Form>
          <Form.Field required error={!isEmpty(validationError)}>
            <label htmlFor='force-guarantee-justification'>Justification</label>
            <TextArea
              id='force-guarantee-justification'
              placeholder='Explain why this guarantee is being forced...'
              value={justification}
              onChange={(e, { value }) => {
                setJustification(value);
                if (validationError && !isEmpty(value.trim())) {
                  setValidationError('');
                }
              }}
              disabled={loading}
              style={{ minHeight: '120px' }}
              data-testid='force-guarantee-justification-input'
            />
          </Form.Field>
        </Form>
        {!isEmpty(validationError) && (
          <Message negative size='mini'>
            <p>{validationError}</p>
          </Message>
        )}
        {errorMessage && (
          <Message negative>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleConfirm}
          disabled={confirmDisabled}
          loading={loading}
          data-testid='force-guarantee-confirm-button'
        >
          Confirm Force Guarantee
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

ForceConfirmationModal.propTypes = {
  open: PropTypes.bool,
  loading: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vin: PropTypes.string,
  productName: PropTypes.string,
  failedRulesCount: PropTypes.number,
  totalRulesCount: PropTypes.number,
};

ForceConfirmationModal.defaultProps = {
  open: false,
  loading: false,
  errorMessage: null,
  orderId: null,
  vin: '',
  productName: '',
  failedRulesCount: 0,
  totalRulesCount: 0,
};

export default ForceConfirmationModal;
